import { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Bike, User, LifeBuoy, Smartphone } from 'lucide-react';
import { uiL } from '../lib/localeDisplay';
import { useAuthStore } from '../store/auth.store';
import { useDemoPersistGuard } from '../hooks/useDemoPersistGuard';
import { connectSocket, disconnectSocket } from '../lib/socket';
import { api } from '../lib/api';
import { nativeApkInstallAddsValue } from '../lib/nativeAppPlus';
import LogoutButton from './LogoutButton';
import BrandWordmark from './BrandWordmark';
import DriverDemoBanner from './DriverDemoBanner';
import DemoPersistToast from './DemoPersistToast';

/** Contexte partagé avec les pages livreur (`/driver`, `/driver/profile`) via `useOutletContext`. */
export interface DriverOutletContext {
  isOnline: boolean;
  setIsOnline: (value: boolean) => void;
  location: { lat: number; lng: number } | null;
  liveDeliveryAlert: string | null;
  geoError: string | null;
}

const ONLINE_STORAGE_PREFIX = 'bienlivrer:driver:isOnline:';
/** Espace minimal entre deux envois de position au serveur (le GPS peut rafraîchir bien plus
 * souvent, notamment en mouvement) — la position affichée localement reste, elle, toujours à jour. */
const LOCATION_SEND_THROTTLE_MS = 15000;

/** Statut "en ligne" démarré par défaut (moins de friction pour prendre son service), mais
 * mémorisé par livreur dès qu'il bascule manuellement — un livreur qui se met hors ligne en fin
 * de tournée ne doit pas se retrouver "en ligne" malgré lui à la prochaine ouverture de l'app. */
function readStoredOnline(userId: string | undefined): boolean {
  if (!userId) return true;
  try {
    const raw = localStorage.getItem(`${ONLINE_STORAGE_PREFIX}${userId}`);
    return raw === null ? true : raw === '1';
  } catch {
    return true;
  }
}

function writeStoredOnline(userId: string | undefined, value: boolean): void {
  if (!userId) return;
  try {
    localStorage.setItem(`${ONLINE_STORAGE_PREFIX}${userId}`, value ? '1' : '0');
  } catch {
    // Stockage indisponible (ex. navigation privée) : sans impact, juste pas de mémorisation.
  }
}

/**
 * Statut "en ligne" + géolocalisation + connexion Socket.IO portés par le layout (et non par
 * `DriverPage`) : ils doivent survivre à la navigation entre les onglets Livraisons/Profil.
 * Avant ce changement, ouvrir le Profil démontait `DriverPage` et coupait silencieusement le
 * GPS et le socket — le livreur restait affiché "en ligne" nulle part et ne recevait plus les
 * nouvelles courses tant qu'il ne rebasculait pas manuellement le statut en revenant.
 */
export default function DriverLayout() {
  const { t, i18n } = useTranslation();
  const L = <T,>(fr: T, de: T, en: T, it: T) => uiL(i18n.language, fr, de, en, it);
  const qc = useQueryClient();
  const { user, accessToken } = useAuthStore();
  const { isDemoDriver } = useDemoPersistGuard();
  const showApkGuide = nativeApkInstallAddsValue(
    typeof navigator === 'undefined' ? '' : navigator.userAgent,
  );

  const [isOnline, setIsOnlineState] = useState(() => user?.isDemo === true || readStoredOnline(user?.id));
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [liveDeliveryAlert, setLiveDeliveryAlert] = useState<string | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const lastLocationSentAtRef = useRef(0);

  const setIsOnline = (value: boolean) => {
    setIsOnlineState(value);
    writeStoredOnline(user?.id, value);
  };

  // Géolocalisation temps réel (simulée en démo livreur)
  useEffect(() => {
    if (!isOnline) { setGeoError(null); return; }
    if (isDemoDriver) {
      setLocation({ lat: 47.1412, lng: 7.2505 });
      setGeoError(null);
      return;
    }
    lastLocationSentAtRef.current = 0; // 1ère position après passage en ligne toujours envoyée immédiatement
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setLocation(coords);
        setGeoError(null);
        const now = Date.now();
        if (now - lastLocationSentAtRef.current >= LOCATION_SEND_THROTTLE_MS) {
          lastLocationSentAtRef.current = now;
          api.patch('/deliveries/location', coords).catch(() => {});
        }
      },
      (err) => {
        setGeoError(
          err.code === err.PERMISSION_DENIED
            ? L(
                'Géolocalisation refusée — autorisez-la dans les réglages du navigateur pour recevoir des courses.',
                'Standortfreigabe verweigert — bitte in den Browsereinstellungen aktivieren, um Aufträge zu erhalten.',
                'Location access denied — enable it in your browser settings to receive deliveries.',
                'Geolocalizzazione rifiutata — autorizzala nelle impostazioni del browser per ricevere le corse.',
              )
            : L(
                'Position GPS indisponible — nouvelle tentative automatique.',
                'GPS-Position nicht verfügbar — automatischer neuer Versuch.',
                'GPS position unavailable — retrying automatically.',
                'Posizione GPS non disponibile — nuovo tentativo automatico.',
              ),
        );
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 10000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, isDemoDriver]);

  // ── Socket.IO — nouvelle course proposée en temps réel (en complément du polling
  // dans DriverPage, conservé en filet de sécurité) ──────────────────────────────
  useEffect(() => {
    if (!isOnline || isDemoDriver || !accessToken || !user?.id) return;
    const s = connectSocket(accessToken);

    const joinDriverRoom = () => s.emit('join:driver', user.id);
    s.on('connect', joinDriverRoom);
    if (s.connected) joinDriverRoom();

    const handleNewDelivery = () => {
      void qc.invalidateQueries({ queryKey: ['driver-active'] });
      void qc.invalidateQueries({ queryKey: ['driver-capacity'] });
      setLiveDeliveryAlert(
        L('Nouvelle course disponible !', 'Neue Lieferung verfügbar!', 'New delivery available!', 'Nuova corsa disponibile!'),
      );
      setTimeout(() => setLiveDeliveryAlert(null), 5000);
    };
    s.on('delivery:new', handleNewDelivery);

    return () => {
      s.off('connect', joinDriverRoom);
      s.off('delivery:new', handleNewDelivery);
      disconnectSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, isDemoDriver, accessToken, user?.id, qc]);

  const linkCls = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-1.5 px-2.5 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap min-h-11 ${
      isActive ? 'bg-white/20 text-white' : 'text-white/80 hover:bg-white/10'
    }`;

  const context: DriverOutletContext = { isOnline, setIsOnline, location, liveDeliveryAlert, geoError };

  return (
    <div className="min-h-[100dvh] bg-gray-50 flex flex-col">
      <nav className="bg-navy-700 text-white shadow-md pt-[env(safe-area-inset-top)]">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 min-h-14 py-2 flex flex-wrap items-center gap-1.5 sm:gap-2">
          <Bike size={22} className="shrink-0" />
          <span className="text-sm sm:text-base mr-1 sm:mr-4 min-w-0 truncate">
            <BrandWordmark inverse className="text-sm sm:text-base" />
            <span className="hidden sm:inline"> — {L('Livreur', 'Fahrer', 'Driver', 'Autista')}</span>
          </span>
          <NavLink to="/driver" end className={linkCls}>
            {L('Livraisons', 'Lieferungen', 'Deliveries', 'Consegne')}
          </NavLink>
          <NavLink to="/driver/profile" className={linkCls}>
            <User size={16} />
            <span className="hidden sm:inline">{L('Profil', 'Profil', 'Profile', 'Profilo')}</span>
          </NavLink>
          <NavLink to="/support" className={linkCls}>
            <LifeBuoy size={16} />
            <span className="hidden sm:inline">{t('nav.support')}</span>
          </NavLink>
          {showApkGuide && (
            <NavLink to="/app-android" className={linkCls}>
              <Smartphone size={16} />
              <span className="hidden sm:inline">{L('App', 'App', 'App', 'App')}</span>
            </NavLink>
          )}
          <LogoutButton variant="nav" className="ml-auto" />
        </div>
      </nav>
      <DriverDemoBanner />
      <DemoPersistToast />
      <main className="flex-1">
        <Outlet context={context} />
      </main>
    </div>
  );
}
